import clsx from 'clsx';
import type { ReactNode } from 'react';

// Shared primitives for the in-card "stat strip" pattern used across
// Budget, Income, Retirement, and other pages: a 2- or 3-column grid of
// label + primary value (+ optional subtext) cells.
//
// The full pattern, assembled from these primitives, looks like:
//   <StatGrid columns={3}>
//     <StatItem
//       label="Savings rate"
//       value={<>{rate}% <ValueUnit>net</ValueUnit></>}
//       valueTone={overspend ? 'neg' : undefined}
//       sub={<StatSubText amount={`${grossRate}%`} unit="gross" />}
//     />
//     ...
//   </StatGrid>
//
// Visual conventions enforced by these primitives:
//   - The primary value is bright (default fg) so it reads first.
//   - Inline units next to the value (ValueUnit) are tertiary grey.
//   - Subtext amounts (StatSubText) are secondary grey; the unit
//     descriptor inside subtext is tertiary grey.
//
// Callers that need to render a delta-tone subtext amount (e.g. a green
// growth number) pass a colored ReactNode into StatSubText's `amount`
// prop — `delta-pos` / `delta-neg` override the secondary color.

// 2- or 3-column grid of StatItems, collapsing to single-column on small
// screens. `gap` matches the common spacing variants used across pages
// (4 = compact in-card strip, 6 = roomier hero strip on long pages).
export function StatGrid({
  columns,
  gap = 4,
  children,
  className,
}: {
  columns: 2 | 3;
  gap?: 3 | 4 | 6;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={clsx(
        'grid',
        columns === 2 ? 'grid-cols-2' : 'grid-cols-3 max-sm:grid-cols-2',
        gap === 3 ? 'gap-3' : gap === 6 ? 'gap-6' : 'gap-4',
        className,
      )}
    >
      {children}
    </div>
  );
}

// One cell in a StatGrid: small uppercase label, primary value, optional
// subtext. `value` is a ReactNode so callers can compose inline units
// (via <ValueUnit>) and tones; `sub` is typically a <StatSubText>.
// `valueSize` defaults to 'lg' (in-card stat strip); hero stat strips at
// the top of long pages use 'xl' for a more prominent feel.
export function StatItem({
  label,
  value,
  valueTone,
  valueSize = 'lg',
  sub,
}: {
  label: ReactNode;
  value: ReactNode;
  valueTone?: 'pos' | 'neg';
  valueSize?: 'lg' | 'xl';
  sub?: ReactNode;
}) {
  return (
    <div>
      <div className="stat-label">{label}</div>
      <div
        className={clsx(
          'tabular font-semibold mt-1',
          // Drop one notch on mobile (where the grid collapses to 2-up) so
          // the numbers don't dominate a narrow column.
          valueSize === 'xl' ? 'text-xl sm:text-2xl' : 'text-base sm:text-lg',
          valueTone === 'pos' && 'delta-pos',
          valueTone === 'neg' && 'delta-neg',
        )}
      >
        {value}
      </div>
      {sub}
    </div>
  );
}

// Inline unit tag next to a stat's primary value (e.g. "$X net",
// "12% gross"). Same muted tertiary color as subtext descriptors but
// renders as a peer of the main value rather than on its own line.
export function ValueUnit({ children }: { children: ReactNode }) {
  return (
    <span className="ml-1.5 text-xs text-fg-tertiary font-medium">{children}</span>
  );
}

// Small subtext under a stat's primary value. Amount uses fg-secondary
// (lighter grey); the unit/descriptor uses fg-tertiary (darker grey).
// Both props are optional so a subtext can be:
//   - amount only       → "{fmt0(value)}"                 (secondary)
//   - unit only         → "cost of carry"                 (tertiary, descriptor)
//   - amount + unit     → "{fmt0(value)} / yr"            (secondary + tertiary)
// `amount` is a ReactNode so callers can pass colored spans (e.g.
// `delta-pos` for a green growth number) — its own color overrides
// secondary.
export function StatSubText({
  amount,
  unit,
  className,
}: {
  amount?: ReactNode;
  unit?: ReactNode;
  className?: string;
}) {
  if (amount == null && unit == null) return null;
  return (
    <div className={clsx('tabular text-xs mt-0.5', className)}>
      {amount != null && <span className="text-fg-secondary">{amount}</span>}
      {amount != null && unit != null && ' '}
      {unit != null && <span className="text-fg-tertiary">{unit}</span>}
    </div>
  );
}
